/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { get } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import SoftwareAppsApi from 'client/api/v2/SoftwareAppsApi';
import SoftwareAppsStatusApi from 'api/v2/SoftwareAppsStatusApi';
import SoftwareAppsStatusUtil from 'client/util/SoftwareAppsStatusUtil';
import Vue from 'vue';

export class SoftwareAppsStatusCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    // eslint-disable-next-line max-len
    module.getters.getBoundSystemsEligibleForRetry = (...params) => this.getBoundSystemsEligibleForRetry(...params);
    module.getters.getBoundSystemIds = (...params) => this.getBoundSystemIds(...params);
    module.actions.bulkRetryInstall = (...params) => this.bulkRetryInstall(...params);
    module.getters.isItemSelectable = (...params) => this.isItemSelectable(...params);
    module.actions.setIsAppleVpp = (...params) => this.setIsAppleVpp(...params);
    module.actions.setSoftwareAppsId = (...params) => this.setSoftwareAppsId(...params);
    module.actions.retryInstall = (...params) => this.retryInstall(...params);

    module.mutations.SET_IS_APPLE_VPP = (...params) => this.SET_IS_APPLE_VPP(...params);
    module.mutations.SET_SOFTWAREAPPS_ID = (...params) => this.SET_SOFTWAREAPPS_ID(...params);
    module.mutations.SET_TOTAL_COUNT = (...params) => this.SET_TOTAL_COUNT(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  getBoundSystemIds(state) {
    const selectedIds = get(state, 'features.selectedItems.ids', []);
    const boundItems = get(state, 'entities.boundItems.byId', {});
    const boundSystemIds = selectedIds.map((id) => boundItems[id].systemId);
    return boundSystemIds;
  }

  getBoundSystemsEligibleForRetry(state = { entities: { boundItems: { totalCount: 0 } } }) {
    const { isAppleVpp } = state.entities;
    const { selectedItems } = state.features;
    const arrayOfSystems = Object.values(state.entities.items.byId);
    const arrayOfSystemsSelected = arrayOfSystems.filter(obj => selectedItems.ids.includes(obj.id));
    const numOfRetryLinks = arrayOfSystemsSelected.reduce((accumulator, id) => {
      const isNotAllowedForRetry = SoftwareAppsStatusUtil
        .notAllowedForRetry({ fullStatus: id.state, isAppleVpp });
      if (!isNotAllowedForRetry) {
        accumulator += 1;
      }
      return accumulator;
    }, 0);
    return numOfRetryLinks;
  }

  isItemSelectable(state) {
    return (item) => {
      const { isAppleVpp } = get(state, 'entities', false);
      const fullStatus = item.state;
      const isNotAllowedForRetry = SoftwareAppsStatusUtil
        .notAllowedForRetry({ fullStatus, isAppleVpp });

      return !isNotAllowedForRetry;
    };
  }

  // ----------------------------------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------------------------------

  /**
   * Override action from base class to evaluate if all items have been fetched
   * and update totalCount correctly.
   * This override is needed because softwareApps status values fetched can contain entries
   * related to deleted systems, which are filtered out by SoftwareAppsStatusApi.
   * This, however, can cause a mismatch between the totalCount received by the backend
   * and the actual total number of entries displayed in the itemTable.
   * We also need to keep the original totalCount so that the itemTable can fetch all
   * the possible status values correctly until we find all possible data.
   */
  async fetchCollection(context, { next = false } = {}) {
    // super.fetchCollection is called at the start,
    // as it sets the totalCount state value that we're checking here.
    const results = await super.fetchCollection(context, { next });

    const { state } = context;
    const { skip, limit } = state.features.items.pagination;
    const { ids } = state.features.items;
    const { totalCount } = state.entities.items;

    const isTotalCountSet = !!totalCount;
    const areAllItemsFetched = skip + limit >= totalCount;

    if (isTotalCountSet && areAllItemsFetched) {
      // this will set the totalCount value to the actual amount of software apps status entries
      // with existing devices, but only when all data has been fetched
      context.commit('SET_TOTAL_COUNT', { totalCount: ids.length });
    }

    return results;
  }

  setIsAppleVpp({ commit }, { isAppleVpp }) {
    commit('SET_IS_APPLE_VPP', { isAppleVpp });
  }

  setSoftwareAppsId({ commit }, { softwareAppsId }) {
    commit('SET_SOFTWAREAPPS_ID', { softwareAppsId });
  }

  retryInstall(context, { systemId }) {
    const { softwareAppsId } = context.state.entities;
    return SoftwareAppsApi.retryInstall(softwareAppsId, { system_ids: [systemId] });
  }

  bulkRetryInstall(context) {
    const { softwareAppsId } = context.state.entities;
    const selectedSystemIds = context.getters.getBoundSystemIds;
    return SoftwareAppsApi.retryInstall(softwareAppsId, { system_ids: selectedSystemIds });
  }

  // ----------------------------------------------------------------------------------------------
  // Mutations
  // ----------------------------------------------------------------------------------------------
  SET_IS_APPLE_VPP(state = { entities: { isAppleVpp: {} } }, { isAppleVpp }) {
    Vue.set(state.entities, 'isAppleVpp', isAppleVpp);
  }

  SET_SOFTWAREAPPS_ID(state = { entities: { softwareAppsId: {} } }, { softwareAppsId }) {
    state.entities.softwareAppsId = softwareAppsId;
    this.Api.setSoftwareAppId(softwareAppsId);
  }

  SET_TOTAL_COUNT(state, { totalCount }) {
    if (typeof totalCount === 'number') {
      state.entities.items.totalCount = totalCount;
    }
  }
}

export default new SoftwareAppsStatusCollectionClass(SoftwareAppsStatusApi, 'id', '').extractModule();
